import React, { forwardRef } from 'react'
import { CSVLink } from "react-csv";
import {
    DataTable,
    Table,
    Link,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Modal,
    TextInput,
    Select,
    SelectItem,
    Form,
    DatePicker,
    DatePickerInput,
    OverflowMenu,
    OverflowMenuItem,
    InlineNotification,
    NotificationActionButton
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Save16 as Save,
    Download16 as Download,
    Edit32 as Edit,
} from '@carbon/icons-react';

const clientPhoneHeaders = [
    { header: "ID", key: "id" },
    { header: "Type", key: "type" },
    { header: "Action", key: "action" },
    { header: "Start Date", key: "startDate" },
    { header: "End Date", key: "endDate" },
    { header: "Email Address", key: "email" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const ClientEmailAddresses = forwardRef(({ setEmailToDel, emailToDel, setOpenDeleteEmailModal, openDeleteEmailModal, deleteEmail, curDate, addEmailForm, addEmail, countries, rows, totalEmailItems, currentEmailPageSize, setEmailCurrentPageSize, setFirstRowEmailIndex, openAddEmailModal, setOpenAddEmailModal, openEditEmailModal, setOpenEditEmailModal, editEmailRecord, editEmailSetup, editEmail, editEmailForm, editError, setEditError }: any, ref) => (
    <>
        <DataTable rows={rows} headers={clientPhoneHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getToolbarProps,
                getBatchActionProps,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        className="mt-1"
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddEmailModal(true)}>
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "email" ? <p>{cell.value}</p>
                                                    : cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                        <OverflowMenuItem onClick={() => setOpenEditEmailModal(true) & editEmailSetup(row.id)} itemText="Edit" />
                                                        <OverflowMenuItem onClick={() => setEmailToDel(row.id) & setOpenDeleteEmailModal(true)} hasDivider isDelete itemText="Delete" />
                                                    </OverflowMenu>
                                                        : cell.info.header === "type" ? <p>{cell.value.type}</p>
                                                            : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalEmailItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentEmailPageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentEmailPageSize) {
                                    setEmailCurrentPageSize(pageSize);
                                }
                                setFirstRowEmailIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddEmailModal}
            modalHeading="Add email address"
            modalLabel="Client Email Addresses"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddEmailModal(false) & setEditError(false)}
            onRequestSubmit={() => addEmailForm.current && addEmailForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add client phone number" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new phone number. You may add new Home, Business, Mobile, or Fax numbers.
            </p>
            <Form onSubmit={(values) => addEmail(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="yyyy-mm-dd"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={curDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="yyyy-mm-dd"
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005">
                    <Select id="type" name="type" defaultValue="2" labelText="Email Type" style={{ marginBottom: '1rem' }}>
                        <SelectItem value="1" text="Home" />
                        <SelectItem value="2" text="Business" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="email"
                        name="email"
                        type="email"
                        labelText="Email Address"
                        placeholder="Enter email address"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addEmailForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditEmailModal}
            modalHeading="Edit email address"
            modalLabel="Client Email Addresses"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditEmailModal(false) & setEditError(false)}
            onRequestSubmit={() => editEmailForm.current && editEmailForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to edit client phone number" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected email address.
            </p>
            <Form onSubmit={(values) => editEmail(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="yyyy-mm-dd"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={editEmailRecord?.startDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="yyyy-mm-dd"
                            defaultValue={editEmailRecord?.endDate}
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005">
                    <Select id="type" name="type" defaultValue="2" labelText="Email Type" style={{ marginBottom: '1rem' }}>
                        <SelectItem selected={editEmailRecord?.type.type === 'Home' ? "selected" : ""} value="1" text="Home" />
                        <SelectItem selected={editEmailRecord?.type.type === 'Business' ? "selected" : ""} value="2" text="Business" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="email"
                        name="email"
                        type="email"
                        invalidText="Email address is required"
                        defaultValue={editEmailRecord?.email}
                        labelText="Email Address"
                        placeholder="Enter email address"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editEmailForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeleteEmailModal}
            modalHeading="Delete Client Email Address"
            modalLabel="Client Email Addresses"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeleteEmailModal(false) & setEditError(false)}
            onRequestSubmit={() => deleteEmail(emailToDel)}
        >
            {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete client email address" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected client email address?</p>

        </Modal>
    </>
));